import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import { ProviderBalanceService } from '@services/provider/provider-balance.service';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

export type RequestStatus = 'PENDING_PAYMENT' | 'PENDING_VERIFICATION';

export interface RequestsQueryDTO {
	page?: number;
	limit?: number;
	sort_by?: string;
	sort_order?: 'asc' | 'desc';
	status?: RequestStatus;
}

export interface RequestItem {
	id: number;
	externalOrderId: number | null;
	userId: number;
	platformId: number;
	productId: number;
	providerId: number;
	url: string;
	amount: number;
	price: number;
	customComments: string | null;
	paymentStatus: string;
	providerStatus: string;
	paymentMethod: string | null;
	createdAt: string;
	updatedAt: string;
	user: {
		id: number;
		name: string | null;
		lastName: string | null;
		email: string;
	} | null;
	product: {
		id: number;
		name: string;
	} | null;
	platform: {
		id: number;
		name: string;
	} | null;
}

export interface RequestsListResponse {
	data: RequestItem[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

export interface RequestActionResponse {
	success: boolean;
	orderId: number;
	paymentStatus: string;
	providerStatus: string;
	providerOrderId?: number | null;
	message: string;
}

@Injectable({
	providedIn: 'root',
})
export class RequestsService {
	private readonly baseUrl = `${environment.pathBackend}/requests`;

	private providerBalance = inject(ProviderBalanceService);

	constructor(private http: HttpClient) {}

	getRequests(params: RequestsQueryDTO = {}): Observable<RequestsListResponse> {
		const { page = 1, limit = 10, sort_by, sort_order, status } = params;

		let httpParams = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

		if (sort_by) httpParams = httpParams.set('sort_by', sort_by);
		if (sort_order) httpParams = httpParams.set('sort_order', sort_order);
		if (status) httpParams = httpParams.set('status', status);

		return this.http.get<RequestsListResponse>(this.baseUrl, { params: httpParams }).pipe(
			catchError(() => {
				return throwError(() => new Error('Error al cargar las solicitudes'));
			}),
		);
	}

	approveRequest(id: number): Observable<RequestActionResponse> {
		// The HTTP error goes through untouched: the caller shows the server's (translated) message
		return this.http
			.post<RequestActionResponse>(`${this.baseUrl}/${id}/approve`, {})
			.pipe(finalize(() => this.providerBalance.refresh()));
	}

	rejectRequest(id: number): Observable<RequestActionResponse> {
		return this.http.post<RequestActionResponse>(`${this.baseUrl}/${id}/reject`, {});
	}
}
